import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Wallet, TrendingUp, TrendingDown, Eye, Heart } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useMosque } from '../contexts/MosqueContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialWidget = () => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { mosqueId, to } = useMosque();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!settings.show_financial_widget) { setSummary(null); return; }
    axios
      .get(`${API_URL}/api/finance/summary`, { params: mosqueId ? { mosque_id: mosqueId } : {} })
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [settings.show_financial_widget, mosqueId]);

  const formatRupiah = (amount) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);

  if (!settings.show_financial_widget || !summary) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-6 pb-8"
      data-testid="financial-widget"
    >
      <div className="bg-white rounded-3xl shadow-lg border border-stone-200 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-white" />
            <h3 className="text-lg font-heading font-semibold text-white">
              {settings.widget_title || 'Transparansi Keuangan'}
            </h3>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 gap-4">
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <p className="text-sm font-body text-emerald-800 font-medium">Total Kas</p>
                  <p className="text-xs font-body text-emerald-600">
                    Per {new Date(summary.month).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-body font-bold text-emerald-800">{formatRupiah(summary.total_kas)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <p className="text-xs font-body text-green-800 font-medium">Pemasukan</p>
                </div>
                <p className="text-lg font-body font-bold text-green-700">{formatRupiah(summary.monthly_income)}</p>
              </div>

              <div className="p-4 bg-red-50 rounded-2xl">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  <p className="text-xs font-body text-red-800 font-medium">Pengeluaran</p>
                </div>
                <p className="text-lg font-body font-bold text-red-700">{formatRupiah(summary.monthly_expense)}</p>
              </div>
            </div>
          </div>

          <button
            data-testid="widget-donate-now-btn"
            onClick={() => navigate(to('/infaq'))}
            className="mt-5 w-full bg-emerald-700 hover:bg-emerald-800 text-white font-body font-semibold py-3 rounded-full transition-colors flex items-center justify-center gap-2"
          >
            <Heart className="w-5 h-5" />
            Donasi Sekarang
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default FinancialWidget;
